import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, lastValueFrom, map } from 'rxjs';
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, PopupRequest, SilentRequest } from '@azure/msal-browser';

import { IValidatedUser } from '@models/validated-user';
import { environment } from '@environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { User } from '@models/user';
import { InternalDomain } from '@models/internal-domain';
import { GlobalService } from './global.service';
import { UserMaintenanceInfo } from '@models/user-maintenance-info';
import { Account } from '@models/account';
import { AccountService } from './account.service';
import { SetupInfo } from '@models/setup-info';
import { Route, Router } from '@angular/router';
import { DynamicPageComponent } from '../dynamic-page/dynamic-page.component';
import { hasAccountSelected, isLoggedIn } from './can-activate.guard';

@Injectable({
  providedIn: 'root'
})
export class UserService {
    private authorizationState: BehaviorSubject<IValidatedUser | null> = new BehaviorSubject(null);
    authorizationState$: Observable<IValidatedUser | null> = this.authorizationState.asObservable();

    private accountState: BehaviorSubject<Account | null> = new BehaviorSubject(null);
    accountState$: Observable<Account | null> = this.accountState.asObservable();

    private readonly _accountNumber = signal<Account>(null);
    AccountNumber$ = this._accountNumber.asReadonly();

    private _setupMenuItems = signal<SetupInfo[]>([]);
    SetupInfoItems$ = this._setupMenuItems.asReadonly();

    // private _accountService: AccountService = inject(AccountService);

    private internalToken: string;

    private baseUserUrl: string = environment.ApiBase + "users/";

    // private currentUser: Subject<IValidatedUser> = new Subject<IValidatedUser>();
    // currentUser$: Observable<IValidatedUser> = this.currentUser.asObservable();

    // private currentAccount: Subject<CustomerAccount> = new Subject<CustomerAccount>();
    // currentAccount$: Observable<CustomerAccount> = this.currentAccount.asObservable();

    constructor(
        private _http: HttpClient,
        private _authService: MsalService,
        private _globalService: GlobalService,
        private _authBroadcastService: MsalBroadcastService,
        private _router: Router,
        private _accountService: AccountService
    ) { }

    // initializeMsal(): void {
    //     this._authBroadcastService.msalSubject$
    //         .pipe(
    //             filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
    //         )
    //         .subscribe((result: EventMessage) => {
    //             console.log(result);
    //             const payload = result.payload as AuthenticationResult;
    //             this._authService.instance.setActiveAccount(payload.account);
    //         });

    // }

    async signOutUser(): Promise<void> {
        // console.log('signOutUser start');
        if (this.isAuthenticated()) {
            let currentAccount: AccountInfo = this._authService.instance.getActiveAccount();
            const logoutHint = currentAccount.idTokenClaims.login_hint;
            this._authService.logoutRedirect({
                logoutHint: logoutHint,
                postLogoutRedirectUri: window.location.origin
            });
        }

        this.clearCurrentUser();
        // console.log('signOutUser end');
    }

    async getDomainList(): Promise<InternalDomain[]> {
        return firstValueFrom(this._globalService.getDomainList()).then(
            (domainList: InternalDomain[]) => {
                return domainList;
            }
        );
    }

    async isInternalDomain(emailAddress: string): Promise<boolean> {
        let domains: InternalDomain[] = await this.getDomainList();

        emailAddress = emailAddress.toLowerCase();
        const domainList: InternalDomain[] = domains.filter(
            (domain: InternalDomain) => {
                if (emailAddress.endsWith('@' + domain.Id.toLowerCase())) {
                    return domain;
                }
                return null;
            }
        );

        return domainList.length > 0;
    }

    loginUser(data: any): Observable<boolean> {
        const headers: HttpHeaders = new HttpHeaders({ "Content-Type": "application/json" });

        return this._http.post<IValidatedUser>(this.baseUserUrl + "user-login", data, { headers: headers }).pipe(
            map((result: IValidatedUser) => {
                this.setCurrentUser(result);
                this.authorizationState.next(result);
                return result !== null && result !== undefined;
            })
        );
    }

    async loginInternalUser(userId: string): Promise<void> {
        console.log('loginInternalUser start');
        // HACK because of bug in MSAL where value doesn't get cleared upon sign-out:
        //sessionStorage.removeItem('msal.interaction.status');
        // HACK because of bug where renewing token fails
        // localStorage.clear();

        let req: PopupRequest = {
            authority: 'https://login.microsoftonline.com/usnr.onmicrosoft.com',
            scopes: [],
            prompt: "login",
            loginHint: userId
        }

        await firstValueFrom(this._authService.loginPopup(req));

        // .then(
        //     (result: AuthenticationResult) => {
        //         this._authService.instance.setActiveAccount(result.account);
        //     });

        // if (this._authService.instance.getActiveAccount() !== null) {
        //     await this.createValidatedUser();
        // }
        // return this._authService.acquireTokenPopup(req).pipe(
        //     map((response: AuthenticationResult) => {
        //         console.log('acquireTokenPopup success');
        //         this._authService.instance.setActiveAccount(response.account);
        //         this.internalToken = response.idToken;

        //         if (this.internalToken && this.internalToken !== '') {
        //             console.log('acquireTokenPopup success/internalToke = ' + this.internalToken);
        //             this.createValidatedUser().then();
        //         }
        //     })
        // );
    }

    async setInternalAccount(account: AccountInfo, token: string): Promise<void> {
        this._authService.instance.setActiveAccount(account);
        this.internalToken = token;
        if (this.internalToken && this.internalToken !== '') {
            await this.createValidatedUser();
        }
    }

    isAuthenticated(): boolean {
        return this._authService.instance.getActiveAccount() !== null;
    }

    async createValidatedUser(): Promise<void> {
        await this.loginAuthenticatedUser();
    }

    getCurrentUser(): IValidatedUser {
        const user: string = localStorage.getItem("currentUser");

        if (user !== null && user !== undefined && user !== "") {
            const validatedUser: IValidatedUser = JSON.parse(user);
            return validatedUser;
        }

        return null;
    }

    async canView(entity: string): Promise<boolean> {
        const params: HttpParams = new HttpParams()
            .append('entity', entity);
        return await lastValueFrom(this._http.get<boolean>(this.baseUserUrl + "view", { params }));
    }

    async canEdit(entity: string): Promise<boolean> {
        const params: HttpParams = new HttpParams()
            .append('entity', entity);
        return await lastValueFrom(this._http.get<boolean>(this.baseUserUrl + "edit", { params }));
    }

    validateCurrentUser(user: IValidatedUser): void {
        // console.log('validateCurrentUser start');
        if (user) {
            this._http.get<IValidatedUser>(this.baseUserUrl + "validate-user").subscribe({
                next: (response) => {
                    if (!response) {
                        this.clearCurrentUser();
                        // console.log('validateCurrentUser end/success');
                    }
                },
                error: (error: any) => { console.error(error); }
            });
        }
    }

    clearCurrentUser(): void {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("token");
        localStorage.removeItem("customerAccount");
        this.accountState.next(null);
        this.authorizationState.next(null);
        this._authService.instance.setActiveAccount(null);
        this._accountNumber.set(null);
    }

    public setCurrentUser(user: IValidatedUser): boolean {
        if (!user) {
            this.clearCurrentUser();
            return false;
        }

        const storedUser: string = JSON.stringify({
            UserId: user.UserId,
            Name: user.Name
        });
        localStorage.setItem("currentUser", storedUser);
        localStorage.setItem("token", user.Token);
        this.authorizationState.next(user);
        return true;
    }

    async loginAuthenticatedUser(): Promise<boolean> {
        // console.log('loginAuthenticatedUser start');
        if (this._authService.instance) {
            var accountInfo: AccountInfo = this._authService.instance.getActiveAccount();
            if (accountInfo) {
console.log('loginAuthenticatedUser authenticated');
                await this.getAuthenticationToken();
            } else {
console.log('loginAuthenticatedUser need token');
                await this.getAuthenticationToken();
            }
        }

        // console.log('loginAuthenticatedUser internal token: ' + this.internalToken);
        if (this.internalToken && this.internalToken !== '') {
            // console.log('loginAuthenticatedUser validate user');
            let authToken: string = this.internalToken;
            const data = { userId: accountInfo.username, token: authToken };

            return await firstValueFrom(this._http.post<IValidatedUser>(this.baseUserUrl + "internal-login", data))
                .then((user: IValidatedUser) => {
                    if (user.Name === "" || user.Name === null) {
                        user.Name = accountInfo.username;
                    }

                    // console.log('loginAuthenticatedUser end/success');
                    return this.setCurrentUser(user);
                });
        } else {
            this.clearCurrentUser();
            // this.signOutUser();
        }

        // localStorage.removeItem("customerAccount");

        // console.log('loginAuthenticatedUser end/failure');
        return false;
    }

    private async getAuthenticationToken(): Promise<string> {
        // console.log('getAuthenticationToken start');
        this.internalToken = '';
        let req: SilentRequest = {
            authority: 'https://login.microsoftonline.com/usnr.onmicrosoft.com',
            scopes: ['User.Read']
        }
        let self = this;
        await firstValueFrom(this._authService.acquireTokenSilent(req)).then((authResult: AuthenticationResult) => {
            self.internalToken = authResult.idToken;
console.log('getAuthenticationToken acquireTokenSilent success');
        })
        .catch(async (error) => {
console.log('getAuthenticationToken acquireTokenSilent failure - ' + typeof(error));
            // if (error instanceof InteractionRequiredAuthError) {
            if (error.errorCode === "interaction_required" || error.errorCode === "consent_required" || error.errorCode === "login_required") {
                let user: IValidatedUser = this.getCurrentUser();
console.log('getAuthenticationToken acquireTokenSilent failure - get new token for ' + user.UserId);
                // this.clearCurrentUser();
                // get new token
                this.loginInternalUser(user.UserId);
            }
console.log('getAuthenticationToken acquireTokenSilent failure');
        });

        // console.log('getAuthenticationToken end ' + this.internalToken);
        return this.internalToken;
    }

    getUsers(searchCriteria: string): Observable<User[]> {
        let params: HttpParams = new HttpParams()
            .set('searchCriteria', searchCriteria);

        return this._http.get<User[]>(`${this.baseUserUrl}userlist`, {
            params: params
        });
    }

    async updateUser(editedUser: User): Promise<boolean> {
        const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
        return await firstValueFrom(this._http.put(`${this.baseUserUrl}update`, editedUser, { headers: headers})).then();
    }

    async updateUserAccounts(editedUser: User, accounts: Account[]): Promise<boolean> {
        const headers: HttpHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
        const params: HttpParams = new HttpParams()
            .set("userId", editedUser.Id);
        return await firstValueFrom(this._http.put(`${this.baseUserUrl}update/accounts`, accounts, { headers: headers, params: params})).then();
    }

    async addUser(newUser: User): Promise<boolean> {
        return await firstValueFrom(this._http.post(`${this.baseUserUrl}add`, newUser)).then();
    }

    async deleteUser(user: User): Promise<boolean> {
        let params: HttpParams = new HttpParams()
            .set('userId', user.Id);

        if (user.Id == this.getCurrentUser().UserId) {
            alert('Current user cannot be deleted');
        }

        return await firstValueFrom(this._http.delete(`${this.baseUserUrl}delete`, { params: params })).then();
    }

    getUserMaintenanceInfo(): Observable<UserMaintenanceInfo> {
        let params: HttpParams = new HttpParams();
        params = params.append('language', this._globalService.getLanguageCode());

        return this._http.get<UserMaintenanceInfo>(this.baseUserUrl + 'user-maintenance-info', { params: params });

    }

    setCustomerAccount(account: Account): void {
        localStorage.setItem("customerAccount", JSON.stringify(account));
        this.accountState.next(account);
        this._accountNumber.set(account);
        this.setupReports(account);
        // if (account) {
        //     this._accountService.getReportSetupForAccount(account.AccountNumber, this._globalService.getLanguageCode() ).subscribe(
        //         (setupInfo: SetupInfo[]) => {
        //             setupInfo.forEach((info: SetupInfo) => {
        //                 console.log('Report Setup Info: ' + info.page + ' - ' + info.route);
        //             });
        //         }
        //     );
        // }
    }

    getCustomerAccount(): Account {
        const customerAccount: string = localStorage.getItem("customerAccount");
        if (customerAccount && customerAccount !== "") {
            let account: Account = JSON.parse(customerAccount);
            if (account && this._accountNumber() === null) {
                this._accountNumber.set(account);
                this.setupReports(account);
            }
            return JSON.parse(customerAccount);
        }

        return null;
    }

    private async setupReports(account: Account): Promise<void> {
        let menuItems: SetupInfo[] = await firstValueFrom(this._globalService.getSetupInfo()).then(
            (setupInfo: SetupInfo[]) => {
                let routes: Route[] = [];
                setupInfo.forEach((info: SetupInfo) => {
                    routes.push({path: info.route, component: DynamicPageComponent, data: { details: info.details }, canActivate: [ isLoggedIn, hasAccountSelected]});
                });

                let currentRoutes: Route[] = this._router.config;
                this._router.resetConfig([...routes, ...currentRoutes]);

                return setupInfo;
        });

        if (account) {
            let reportItems: SetupInfo[] = await firstValueFrom(this._accountService.getReportSetupForAccount(account.AccountNumber, this._globalService.getLanguageCode())).then(
                (setupInfo: SetupInfo[]) => {
                    let routes: Route[] = [];
                    setupInfo.forEach((info: SetupInfo) => {
                        routes.push({path: info.route, component: DynamicPageComponent, data: { details: info.details }, canActivate: [ isLoggedIn, hasAccountSelected]});
                    });
                    let currentRoutes: Route[] = this._router.config;
                    this._router.resetConfig([...routes, ...currentRoutes]);

                    return setupInfo;
                }
            );

            if (reportItems && reportItems.length > 0) {
                menuItems = menuItems.concat(reportItems);
            }
        }
        this._setupMenuItems.set(menuItems);
    }

    getAccountsForUser(criteria: string, userId: string): Observable<Account[]> {
        const params: HttpParams = new HttpParams({ fromObject: { criteria: criteria, userId: userId } });
        return this._http.get<Account[]>(this.baseUserUrl + "customer-accounts", { params: params });
    }
}

export const canViewUserMaintenance =
    (userService = inject(UserService)) => userService.canView("users");

